/**
 * Illustrated scenery for the expedition map, drawn in natural colours.
 *
 * Unlike the first version's tinted props, these keep their own palette
 * (foliage greens, wood browns, water blues, flower whites and yellows) so a
 * land reads as a place with several colours in it, not a single tint. A
 * light ambient mix toward the land's ground colour keeps them seated.
 * Every prop is positioned by its base point (bottom centre).
 */
import {mix} from './mapColor';

/** Where a prop stands, in canvas pixels. */
export interface WorldPropProps {
  /** Horizontal centre of the prop. */
  x: number;
  /** Ground line the prop stands on. */
  y: number;
  /** Uniform scale. Defaults to 1. */
  scale?: number;
  /** The land's ground colour, blended lightly into every prop. */
  ground: string;
}

const AMBIENT = 0.24;

const LEAF = '#5cb85c';
const LEAF_DARK = '#3f9a49';
const LEAF_LIGHT = '#8fd47a';
const WOOD = '#8a5a3c';
const WOOD_DARK = '#6b4530';
const WATER = '#5fb3e8';
const WATER_DEEP = '#3f93cf';
const SAND = '#e8d6a8';
const STONE = '#c9c4bb';
const STONE_DARK = '#a8a29a';
const PETAL = '#ffffff';
const PETAL_CENTER = '#ffd54a';
const PINK = '#f28bb1';

const transform = ({x, y, scale = 1}: WorldPropProps) =>
  `translate(${x} ${y}) scale(${scale})`;

/** A big round-canopy tree with a lobed, two-tone crown. */
export function BroadTree(props: WorldPropProps) {
  const g = props.ground;
  const leaf = mix(LEAF, g, AMBIENT);
  const leafDark = mix(LEAF_DARK, g, AMBIENT);
  const leafLight = mix(LEAF_LIGHT, g, AMBIENT);
  const wood = mix(WOOD, g, AMBIENT);
  return (
    <g transform={transform(props)}>
      <ellipse cx={0} cy={2} rx={22} ry={5} fill="rgba(0,0,0,0.08)" />
      <path
        d="M-5 0 L-4 -26 L4 -26 L5 0 Z"
        fill={wood}
        stroke={mix(WOOD_DARK, g, AMBIENT)}
        strokeWidth={1.2}
      />
      <path
        d="M-4 -20 L-14 -30"
        stroke={wood}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
      <path
        d="M4 -22 L13 -33"
        stroke={wood}
        strokeWidth={3.5}
        strokeLinecap="round"
      />
      <circle cx={-16} cy={-38} r={17} fill={leafDark} />
      <circle cx={16} cy={-40} r={18} fill={leafDark} />
      <circle cx={0} cy={-52} r={21} fill={leaf} />
      <circle cx={-9} cy={-42} r={16} fill={leaf} />
      <circle cx={10} cy={-46} r={15} fill={leaf} />
      <circle cx={-3} cy={-58} r={9} fill={leafLight} />
      <circle cx={12} cy={-52} r={6} fill={leafLight} />
    </g>
  );
}

/** A conifer with three tiers. */
export function Conifer(props: WorldPropProps) {
  const g = props.ground;
  const leaf = mix(LEAF_DARK, g, AMBIENT);
  const leafLight = mix(LEAF, g, AMBIENT);
  const wood = mix(WOOD_DARK, g, AMBIENT);
  return (
    <g transform={transform(props)}>
      <ellipse cx={0} cy={2} rx={16} ry={4} fill="rgba(0,0,0,0.08)" />
      <rect x={-3.5} y={-16} width={7} height={16} rx={2} fill={wood} />
      <path d="M-22 -14 L0 -40 L22 -14 Z" fill={leaf} />
      <path d="M-17 -30 L0 -52 L17 -30 Z" fill={leafLight} />
      <path d="M-11 -44 L0 -62 L11 -44 Z" fill={leaf} />
    </g>
  );
}

/** A low, two-lobed bush with a couple of berries. */
export function Shrub(props: WorldPropProps) {
  const g = props.ground;
  const leaf = mix(LEAF, g, AMBIENT);
  const leafDark = mix(LEAF_DARK, g, AMBIENT);
  return (
    <g transform={transform(props)}>
      <ellipse cx={0} cy={1} rx={20} ry={4} fill="rgba(0,0,0,0.07)" />
      <ellipse cx={-9} cy={-10} rx={15} ry={11} fill={leafDark} />
      <ellipse cx={9} cy={-11} rx={16} ry={12} fill={leaf} />
      <ellipse
        cx={1}
        cy={-17}
        rx={9}
        ry={7}
        fill={mix(LEAF_LIGHT, g, AMBIENT)}
      />
      <circle cx={-12} cy={-12} r={2.2} fill={PINK} />
      <circle cx={13} cy={-8} r={2.2} fill={PINK} />
    </g>
  );
}

/** A pond with a sandy shore, two lily pads and reeds. */
export function LilyPond(props: WorldPropProps) {
  const g = props.ground;
  const water = mix(WATER, g, AMBIENT);
  const deep = mix(WATER_DEEP, g, AMBIENT);
  const shore = mix(SAND, g, AMBIENT);
  const pad = mix(LEAF, g, AMBIENT);
  return (
    <g transform={transform(props)}>
      <ellipse cx={0} cy={-8} rx={46} ry={19} fill={shore} />
      <ellipse cx={0} cy={-9} rx={39} ry={14} fill={deep} />
      <ellipse cx={0} cy={-10} rx={35} ry={11} fill={water} />
      <ellipse
        cx={-14}
        cy={-14}
        rx={9}
        ry={2.6}
        fill="rgba(255,255,255,0.55)"
      />
      <path
        d="M8 -10 m-7 0 a7 5 0 1 0 14 0 a7 5 0 1 0 -14 0 M8 -10 L14 -13"
        fill={pad}
      />
      <circle cx={20} cy={-7} r={4.5} fill={pad} />
      <circle cx={9} cy={-10} r={1.8} fill={PINK} />
      <path
        d="M-30 -8 L-31 -26 M-26 -8 L-25 -24 M-34 -8 L-36 -22"
        stroke={mix(LEAF_DARK, g, AMBIENT)}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </g>
  );
}

/** A cluster of small daisies. */
export function Daisies(props: WorldPropProps) {
  const stem = mix(LEAF_DARK, props.ground, AMBIENT);
  const flower = (cx: number, cy: number, r: number) => (
    <g key={`${cx}-${cy}`}>
      <path d={`M${cx} 0 L${cx} ${cy + r}`} stroke={stem} strokeWidth={1.6} />
      {[0, 60, 120, 180, 240, 300].map(angle => (
        <ellipse
          key={angle}
          cx={cx}
          cy={cy - r * 0.9}
          rx={r * 0.55}
          ry={r * 0.95}
          fill={PETAL}
          transform={`rotate(${angle} ${cx} ${cy})`}
        />
      ))}
      <circle cx={cx} cy={cy} r={r * 0.55} fill={PETAL_CENTER} />
    </g>
  );
  return (
    <g transform={transform(props)}>
      {flower(-10, -11, 3.6)}
      {flower(4, -16, 4.2)}
      {flower(14, -9, 3.2)}
    </g>
  );
}

/** A pair of rounded rocks. */
export function Rocks(props: WorldPropProps) {
  const g = props.ground;
  return (
    <g transform={transform(props)}>
      <ellipse cx={2} cy={1} rx={22} ry={4} fill="rgba(0,0,0,0.07)" />
      <path
        d="M-20 0 C-22 -12 -10 -18 -2 -14 C2 -12 4 -6 2 0 Z"
        fill={mix(STONE_DARK, g, AMBIENT)}
      />
      <path
        d="M0 0 C-1 -10 8 -16 16 -12 C22 -9 22 -3 20 0 Z"
        fill={mix(STONE, g, AMBIENT)}
      />
      <path
        d="M4 -8 C8 -13 13 -13 16 -11"
        stroke="rgba(255,255,255,0.5)"
        strokeWidth={2}
        strokeLinecap="round"
        fill="none"
      />
    </g>
  );
}

/** A wooden signpost with two boards. */
export function Signpost(props: WorldPropProps) {
  const g = props.ground;
  const wood = mix(WOOD, g, AMBIENT);
  const dark = mix(WOOD_DARK, g, AMBIENT);
  return (
    <g transform={transform(props)}>
      <rect x={-3} y={-46} width={6} height={46} rx={1.5} fill={dark} />
      <path d="M-4 -42 H22 L28 -36 L22 -30 H-4 Z" fill={wood} />
      <path d="M4 -26 H-22 L-28 -20 L-22 -14 H4 Z" fill={wood} />
      <path
        d="M0 -36 H18 M-18 -20 H0"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </g>
  );
}
